// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2022 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

/**
 * <xng-change>
 *  Override thiscomponent, to change when the qty input get displayed and disabled.
 *  In PDP page, it should be always visible, but disabled as needed.
 * </xng-change>
 */
/* eslint-disable spire/export-styles */
import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import { HasProductContext, withProductContext } from "@insite/client-framework/Components/ProductContext";
import logger from "@insite/client-framework/Logger";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import { canAddToCart, hasEnoughInventory } from "@insite/client-framework/Store/Data/Products/ProductsSelectors";
import translate from "@insite/client-framework/Translate";
import TextField, { TextFieldPresentationProps, TextFieldProps } from "@insite/mobius/TextField";
import React, { ChangeEvent, useEffect, useState } from "react";
import { connect } from "react-redux";
import { css } from "styled-components";

interface OwnProps extends TextFieldProps {
    configurationCompleted?: boolean;
    variantSelectionCompleted?: boolean;
    labelOverride?: React.ReactNode;
    extendedStyles?: TextFieldPresentationProps;
    alwaysVisible?: boolean;
}

export interface UnitOfMeasureData {
    UnitOfMeasure: string;
    MinimumOrderQuantity: string;
    MultipleSaleQuantity: string;
}

const mapStateToProps = (
    state: ApplicationState,
    props: { configurationCompleted?: boolean; variantSelectionCompleted?: boolean } & HasProductContext,
) => {
    return {
        canAddToCart: canAddToCart(
            state,
            props.productContext.product,
            props.productContext.productInfo,
            props.configurationCompleted,
            props.variantSelectionCompleted,
        ),
        hasEnoughInventory: hasEnoughInventory(state, props.productContext),
        customSettings: getSettingsCollection(state).customSettings,
    };
};

type Props = OwnProps & ReturnType<typeof mapStateToProps> & HasProductContext;

export const productQuantityOrderedStyles: TextFieldPresentationProps = {
    cssOverrides: {
        formInputWrapper: css`
            input[type="number"]::-webkit-outer-spin-button,
            input[type="number"]::-webkit-inner-spin-button {
                /* ISC-11023 deal with this. */
                /* stylelint-disable-next-line */
                -webkit-appearance: none;
                margin: 0;
            }
        `,
        inputSelect: css`
            /* ISC-11023 deal with this. */
            /* stylelint-disable-next-line */
            -moz-appearance: textfield;
        `,
    },
};

const ProductQuantityOrdered = ({
    hasEnoughInventory,
    productContext: {
        product: { id: productId, properties: productProperties, minimumOrderQty },
        onQtyOrderedChanged,
        productInfo: { qtyOrdered, unitOfMeasure, failedToLoadInventory },
    },
    labelOverride,
    extendedStyles,
    canAddToCart,
    configurationCompleted,
    variantSelectionCompleted,
    alwaysVisible,
    customSettings,
    ...otherProps
}: Props) => {
    const [styles] = useState(() => mergeToNew(productQuantityOrderedStyles, extendedStyles));
    const [productQty, setProductQty] = useState(productProperties.minimumOrderQty || qtyOrdered.toString());
    const unitOfMeasuresData: UnitOfMeasureData[] = JSON.parse(productProperties.unitOfMeasureData || "[]");
    const possibleMinQty = productProperties.minimumOrderQty || null;

    const regexp = /^[0-9\b]+$/;

    const isValidNumberic = (number: string) => {
        if (number === "") {
            // backspace or delete case
            return true;
        }
        return regexp.test(number);
    };
    useEffect(() => {
        if (unitOfMeasuresData && unitOfMeasure) {
            const selectedUOM = unitOfMeasuresData.filter(uom => uom.UnitOfMeasure === unitOfMeasure);
            if (selectedUOM.length) {
                const minQty = parseInt(selectedUOM[0].MinimumOrderQuantity, 10);
                if (qtyOrdered < minQty) {
                    setProductQty(selectedUOM[0].MinimumOrderQuantity);
                    return;
                }
                setProductQty(qtyOrdered.toString());
                return;
            }
            if (qtyOrdered < minimumOrderQty || !qtyOrdered) {
                setProductQty(minimumOrderQty.toString());
                return;
            }
            setProductQty(qtyOrdered.toString());
        }
    }, [unitOfMeasure]);

    useEffect(() => {
        if (qtyOrdered === Number(productQty) || !isValidNumberic(qtyOrdered.toString())) {
            return;
        }
        // search auto-complete part
        if (possibleMinQty && qtyOrdered < parseInt(possibleMinQty, 10)) {
            setProductQty(possibleMinQty);
            return;
        }
        setProductQty(`${qtyOrdered || ""}`);
    }, [qtyOrdered]);

    if (!alwaysVisible && ((!hasEnoughInventory && !failedToLoadInventory) || !canAddToCart)) {
        return null;
    }

    const keyPressHandler = (event: React.KeyboardEvent<HTMLInputElement>) => {
        if (event.which === 13) {
            (event.target as any).blur();
        }
    };

    const qtyChangeHandler = (event: ChangeEvent<HTMLInputElement>) => {
        if (!isValidNumberic(event.currentTarget.value)) {
            event.preventDefault();
            return;
        }
        const value = event.target.value;
        setProductQty(value);

        if (!onQtyOrderedChanged) {
            logger.warn(`There was no onQtyOrderedChanged passed to the ProductContext for ${productId}`);
            return;
        }

        const possibleNumber = Number(value);
        if (!Number.isNaN(possibleNumber)) {
            onQtyOrderedChanged(possibleNumber);
        }
    };

    const qtyOnBlurHandler = (event: React.FocusEvent<HTMLInputElement>) => {
        const value = parseInt(event.currentTarget.value, 10);
        if (unitOfMeasuresData && unitOfMeasure) {
            const selectedUOM = unitOfMeasuresData.filter(uom => uom.UnitOfMeasure === unitOfMeasure);
            if (selectedUOM.length) {
                const minQty = parseInt(selectedUOM[0].MinimumOrderQuantity, 10);
                if (value < minQty) {
                    setProductQty(selectedUOM[0].MinimumOrderQuantity);
                    return;
                }
                setProductQty(event.currentTarget.value);
            }
            if (value < minimumOrderQty || !value || value > 999999999) {
                setProductQty(minimumOrderQty > 0 ? minimumOrderQty.toString() : "1");
                return;
            }
            setProductQty(event.currentTarget.value);
        }
        // search auto-complete part
        if (possibleMinQty && value < parseInt(possibleMinQty, 10)) {
            setProductQty(possibleMinQty);
        }
    };

    return (
        <TextField
            label={labelOverride ?? translate("QTY")}
            value={productQty}
            type="text"
            maxLength={9}
            id={productId}
            data-test-selector="product_qtyOrdered"
            {...otherProps}
            onKeyPress={keyPressHandler}
            onChange={qtyChangeHandler}
            onBlur={qtyOnBlurHandler}
            disabled={
                alwaysVisible && (!productId || !hasEnoughInventory || !canAddToCart)
            }
            {...styles}
        />
    );
};

export default withProductContext(connect(mapStateToProps)(ProductQuantityOrdered));
